<script setup lang="ts">
import { computed } from 'vue';
import { useDebounce } from '@vueuse/core';
import type { FieldSchema } from '../../../types';
import { applyShelterCurrency, toCents } from '@Helpers';
import AFieldLabel from '../../atoms/AFieldLabel/AFieldLabel.vue';
import AFieldMoney from '../../atoms/AFieldMoney/AFieldMoney.vue';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    currency?: string;
    error?: string | boolean;
    field?: FieldSchema;
    placeholder?: string;
    required?: boolean;
  }>(),
  {
    placeholder: 'components.forms.placeholder.common',
  }
);

const CHANGE_DUE_DEBOUNCE_MS = 250;

const model = defineModel<number | string | null>({ default: null });
const debouncedModel = useDebounce(model, CHANGE_DUE_DEBOUNCE_MS);

const showChangeDue = computed(() => changeDueFormatted.value !== null);
const showWarning = computed(() => isAmountLessThanTotal.value);
const totalCharge = computed(() => Number(props.field?.settings?.totalCharge ?? 0));
const collectedAmount = computed(() => {
  const value = debouncedModel.value;
  if (value === null || value === undefined) return null;

  const asNumber = Number(value);
  return Number.isNaN(asNumber) ? null : asNumber;
});
const changeDueCents = computed(() => {
  if (collectedAmount.value === null) return null;

  return toCents(collectedAmount.value) - toCents(totalCharge.value);
});
const changeDueFormatted = computed(() => {
  const notValidChangeDue = changeDueCents.value === null || changeDueCents.value <= 0;
  if (notValidChangeDue) return null;

  const amount = changeDueCents.value / 100;
  const formatted = applyShelterCurrency(amount);

  return formatted.replace(/\(|\)/g, '');
});
const isAmountLessThanTotal = computed(() => {
  if (collectedAmount.value === null) return false;

  return toCents(collectedAmount.value) < toCents(totalCharge.value);
});

function handleKeydown(event: KeyboardEvent) {
  const isDeletingZero = (event.key === 'Backspace' || event.key === 'Delete') && model.value === 0;

  if (isDeletingZero) model.value = null;
}
</script>

<template>
  <fieldset
    :class="[
      'flex flex-col gap-2 rounded-md border border-dashed border-gray-300 bg-slate-50 px-3 pb-3 pt-1',
      { 'border-red-600': showWarning },
    ]"
  >
    <legend :class="['-ml-[13px] px-2 pb-2 pl-0 text-sm font-medium text-gray-600', { 'text-red-600': showWarning }]">
      {{ $t('cart.label.calculateChange') }}
    </legend>

    <AFieldLabel
      :error="showWarning"
      :label="field?.label || field?.title || $t('cart.label.amountCollected')"
      :required="false"
    />

    <AFieldMoney
      v-model="model"
      resetZeroModelOnFocus
      :field
      :currency="field?.settings?.currency || currency"
      :error="showWarning"
      :placeholder="field?.settings?.placeholder || placeholder"
      :required="false"
      @keydown="handleKeydown"
    />

    <Message
      v-if="showWarning"
      pt:text="text-sm"
      severity="warn"
      :closable="false"
    >
      {{ $t('cart.validation.amountCollectedLessThanTotal') }}
    </Message>

    <Message
      v-if="showChangeDue"
      pt:text="text-sm"
      severity="info"
      :closable="false"
    >
      {{ $t('cart.label.changeDue') }}: {{ changeDueFormatted }}
    </Message>
  </fieldset>
</template>
